import axios from 'axios';

const { REACT_APP_FLASK_API_URL } = process.env;

/**
 * Fetch a page of Google reviews for a NAPListing.
 * @param {Object} params
 * @param {string} params.nap_listing - UUID of the NAPListing
 * @param {string} [params.next_page_token] - Token for next page
 * @param {string} [params.sort_by] - Sort order (newestFirst, mostRelevant, etc.)
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const reviewRemovalGoogleReviewsList = async (params = {}) => {
  const response = await axios
    .get(`${REACT_APP_FLASK_API_URL}/dash2-proxy/review-removals/google-reviews/`, {
      params,
    });

  return response;
};

/**
 * List all removal requests for a NAPListing.
 * @param {Object} params - Query parameters
 * @param {string} [params.nap_listing] - UUID of the NAPListing
 * @param {string} [params.status] - Filter by status
 * @param {number} [params.page] - Page number
 * @param {number} [params.page_size] - Results per page
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const reviewRemovalRequestsList = async (params = {}) => {
  const response = await axios
    .get(`${REACT_APP_FLASK_API_URL}/dash2-proxy/review-removals/requests/`, { params });

  return response;
};

/**
 * Create a new review removal request.
 * @param {Object} data
 * @param {string} data.nap_listing - UUID of the NAPListing
 * @param {string} data.review_google_id
 * @param {string} data.review_author
 * @param {number} data.review_rating
 * @param {string} data.review_body
 * @param {string} data.review_created_at - ISO 8601
 * @param {string} data.reason
 * @param {string} [data.notes]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const reviewRemovalRequestsCreate = async (data) => {
  const response = await axios
    .post(`${REACT_APP_FLASK_API_URL}/dash2-proxy/review-removals/requests/`, data);

  return response;
};

/**
 * Retrieve a specific removal request.
 * @param {string} id - Removal request UUID
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const reviewRemovalRequestsRetrieve = async (id) => {
  const response = await axios
    .get(`${REACT_APP_FLASK_API_URL}/dash2-proxy/review-removals/requests/${id}/`);

  return response;
};

/**
 * Partially update a removal request (e.g., change status).
 * @param {string} id - Removal request UUID
 * @param {Object} data - Fields to update
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const reviewRemovalRequestsPartialUpdate = async (id, data) => {
  const response = await axios
    .patch(`${REACT_APP_FLASK_API_URL}/dash2-proxy/review-removals/requests/${id}/`, data);

  return response;
};

// ---------- Helpers ----------

/**
 * Generate a deterministic HSL colour from a string (for avatar backgrounds).
 */
const hashCode = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash); // eslint-disable-line no-bitwise
  }
  return hash;
};

/**
 * Normalise a SERPAPI review object into the shape ReviewsTab expects.
 */
export const normalizeReview = (raw) => ({
  id: raw.review_google_id,
  author: raw.author || 'Anonymous',
  authorAvatarColor: `hsl(${Math.abs(hashCode(raw.author || '')) % 360}, 55%, 55%)`,
  rating: raw.rating,
  body: raw.body || '',
  createdAt: raw.iso_date || raw.created_at || new Date().toISOString(),
  reply: raw.reply
    ? { body: raw.reply, createdAt: raw.reply_date || '' }
    : null,
});

/**
 * Build a reviewSummary from a list of normalised review objects.
 */
export const summarizeReviews = (reviews) => {
  const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  let sum = 0;
  reviews.forEach((r) => {
    distribution[r.rating] = (distribution[r.rating] || 0) + 1;
    sum += r.rating;
  });
  const avg = reviews.length ? sum / reviews.length : 0;

  const now = Date.now();
  const thirtyDays = 30 * 24 * 60 * 60 * 1000;
  const recentCount = reviews.filter(
    (r) => now - new Date(r.createdAt).getTime() <= thirtyDays,
  ).length;
  const unrepliedCount = reviews.filter((r) => !r.reply).length;

  return { count: reviews.length, avg, distribution, recentCount, unrepliedCount };
};

/**
 * Convert a list of backend RemovalRequest objects into the keyed map
 * { [review_google_id]: { reason, notes, requestedAt, status } }
 * that ReviewsTab expects.
 */
export const indexRemovalRequests = (requests) => {
  const map = {};
  const list = Array.isArray(requests) ? requests : requests?.results || [];
  list.forEach((r) => {
    if (!map[r.review_google_id] || new Date(r.added) > new Date(map[r.review_google_id].requestedAt)) {
      map[r.review_google_id] = {
        id: r.id,
        reason: r.reason,
        notes: r.notes,
        requestedAt: r.added,
        status: r.status,
        reviewAuthor: r.review_author,
        reviewRating: r.review_rating,
        reviewBody: r.review_body,
      };
    }
  });
  return map;
};
